import React, { Component } from "react";
import axios from "axios";
import {
  clearDashboardUser,
  clearAccessToken,
  getDashboardUser,
  putAccessToken,
} from "./components/auth/authService";
import { BrowserRouter as Router, Route, Switch } from "react-router-dom";
import "./App.css";
import Navbar from "./components/Navbar";
import LeagueAdmin from "./components/LeagueAdmin";
import CampaignAdmin from "./components/CampaignAdmin";
import ChallengeAdmin from "./components/ChallengeAdmin";
import Login from "./components/auth/LogIn";
import Register from "./components/auth/Register";
import ActivityAdminSelector from "./components/ActivityAdminSelector";
import CompanyAdmin from "./components/CompanyAdmin";
import ImagesAdmin from "./components/ImagesAdmin";
import CampaignStats from "./components/CampaignStats";
import QuizAdminSelector from "./components/QuizAdminSelector";
import { library } from "@fortawesome/fontawesome-svg-core";
import { Auth } from "aws-amplify";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import TeamAdmin from "./components/TeamAdmin";
import IngredientRecipeAdminSelector from "./components/IngredientRecipeAdminSelector";
import MessageBoardFeatureSelector from "./components/MessageBoardFeatureSelector";
import Dashboard from "./components/Dashboard";
import ReportAdmin from "./components/ReportAdmin";
import ModifyPointsAdmin from "./components/ModifyPointsAdmin";
import NotificationAdminSelector from "./components/NotificationAdminSelector";
import UserAdmin from "./components/UserAdmin";
import Hygge from "./components/hygge";

const config = require("./config.json");

library.add(faEdit);

class App extends Component {
  state = {
    isAuthenticated: true,
    isAuthenticating: true,
    user: {},
  };

  isAdminUser = (user) => {
    const groups = user?.signInUserSession?.idToken?.payload?.["cognito:groups"];
    return Array.isArray(groups) && groups.includes("admins");
  };

  checkAuth = () => {
    return this.state.isAuthenticated;
  };

  setAuthStatus = (authenticated) => {
    this.setState({ isAuthenticated: authenticated });
  };

  setUser = (user) => {
    this.setState({ user: user });
  };

  handleLogout = async () => {
    this.setUser({});
    this.setAuthStatus(false);
    clearDashboardUser();
    clearAccessToken();

    try {
      await Auth.signOut();
    } catch (error) {
      if (error !== "No current user") {
        console.log("error signing out: ", error);
      }
    }
  };

  setDashboardSession = (user) => {
    clearAccessToken();
    this.setUser(user);
    this.setAuthStatus(true);
    this.setState({ isAuthenticating: false });
  };

  getCompanyIdFromEmail = async (email) => {
    if (!email) {
      return null;
    }

    try {
      const response = await axios.post(
        `${config.api.invokeUrl}/users/companycheck`,
        { email },
      );
      return response.data?.company ?? null;
    } catch (error) {
      return null;
    }
  };

  setSession = async () => {
    try {
      console.log("Setting session and token");

      const user = await Auth.currentAuthenticatedUser();
      user.Session = await Auth.currentSession();
      user.admin = this.isAdminUser(user);
      if (!user.admin) {
        user.companyId = await this.getCompanyIdFromEmail(user.attributes?.email);
      }
      this.setUser(user);

      putAccessToken(user.signInUserSession.idToken.jwtToken);
      this.setAuthStatus(true);
    } catch (error) {
      const dashboardUser = getDashboardUser();
      if (dashboardUser) {
        this.setDashboardSession(dashboardUser);
        return;
      }

      clearDashboardUser();
      clearAccessToken();
      this.setAuthStatus(false);
    }
    this.setState({ isAuthenticating: false });
  };

  componentDidMount() {
    this.setSession();
  }

  render() {
    const authProps = {
      user: this.state.user,
      setAuthStatus: this.setAuthStatus,
      setUser: this.setUser,
      setSession: this.setSession,
      setDashboardSession: this.setDashboardSession,
      handleLogout: this.handleLogout,
      checkAuth: this.checkAuth,
    };

    if (this.state.isAuthenticating) {
      return null;
    }

    return (
      <div className="App default-font">
        <Router>
          <div>
            {this.state.user.admin && <Navbar auth={authProps} />}

            <Switch>
              <Route
                exact
                path="/"
                render={(props) => (
                  <ProtectedRoute
                    {...props}
                    auth={authProps}
                    component={Dashboard}
                  />
                )}
              />
              <Route
                exact
                path="/login"
                render={(props) => <Login {...props} auth={authProps} />}
              />
              <Route
                exact
                path="/hygge"
                render={(props) => <Hygge {...props} auth={authProps} />}
              />
              <Route
                exact
                path="/register"
                render={(props) => <Register {...props} auth={authProps} />}
              />

              {this.state.user.admin && (
                <>
                  <Route
                    exact
                    path="/ligaer"
                    render={(props) => (
                      <ProtectedRoute
                        {...props}
                        auth={authProps}
                        component={LeagueAdmin}
                      />
                    )}
                  />
                  <Route
                    exact
                    path="/kampagner"
                    render={(props) => (
                      <ProtectedRoute
                        {...props}
                        auth={authProps}
                        component={CampaignAdmin}
                      />
                    )}
                  />
                  <Route
                    exact
                    path="/dashboard"
                    render={(props) => (
                      <ProtectedRoute
                        {...props}
                        auth={authProps}
                        component={Dashboard}
                      />
                    )}
                  />
                  <Route
                    exact
                    path="/challenges"
                    render={(props) => (
                      <ProtectedRoute
                        {...props}
                        auth={authProps}
                        component={ChallengeAdmin}
                      />
                    )}
                  />
                  <Route
                    exact
                    path="/aktiviteter"
                    render={(props) => (
                      <ProtectedRoute
                        {...props}
                        auth={authProps}
                        component={ActivityAdminSelector}
                      />
                    )}
                  />

                  <Route
                    exact
                    path="/quiz"
                    render={(props) => (
                      <ProtectedRoute
                        {...props}
                        auth={authProps}
                        component={QuizAdminSelector}
                      />
                    )}
                  />

                  <Route
                    exact
                    path="/billeder"
                    render={(props) => (
                      <ProtectedRoute
                        {...props}
                        auth={authProps}
                        component={ImagesAdmin}
                      />
                    )}
                  />
                  <Route
                    exact
                    path="/virksomheder"
                    render={(props) => (
                      <ProtectedRoute
                        {...props}
                        auth={authProps}
                        component={CompanyAdmin}
                      />
                    )}
                  />
                  <Route
                    exact
                    path="/hold"
                    render={(props) => (
                      <ProtectedRoute
                        {...props}
                        auth={authProps}
                        component={TeamAdmin}
                      />
                    )}
                  />
                  <Route
                    exact
                    path="/anmeldelser"
                    render={(props) => (
                      <ProtectedRoute
                        {...props}
                        auth={authProps}
                        component={ReportAdmin}
                      />
                    )}
                  />
                  <Route
                    exact
                    path="/kogebog"
                    render={(props) => (
                      <ProtectedRoute
                        {...props}
                        auth={authProps}
                        component={IngredientRecipeAdminSelector}
                      />
                    )}
                  />
                  <Route
                    exact
                    path="/notifikation"
                    render={(props) => (
                      <ProtectedRoute
                        {...props}
                        auth={authProps}
                        component={NotificationAdminSelector}
                      />
                    )}
                  />

                  <Route
                    exact
                    path="/brugere"
                    render={(props) => (
                      <ProtectedRoute
                        {...props}
                        auth={authProps}
                        component={UserAdmin}
                      />
                    )}
                  />
                  <Route
                    exact
                    path="/stats"
                    render={(props) => (
                      <ProtectedRoute
                        {...props}
                        auth={authProps}
                        component={CampaignStats}
                      />
                    )}
                  />
                  <Route
                    exact
                    path="/modifypoints"
                    render={(props) => (
                      <ProtectedRoute
                        {...props}
                        auth={authProps}
                        component={ModifyPointsAdmin}
                      />
                    )}
                  />
                  <Route
                    exact
                    path="/messageboard"
                    render={(props) => (
                      <ProtectedRoute
                        {...props}
                        auth={authProps}
                        component={MessageBoardFeatureSelector}
                      />
                    )}
                  />
                </>
              )}
            </Switch>
          </div>
        </Router>
      </div>
    );
  }
}

export default App;
